import { useState, useEffect, useCallback } from 'react';

interface SpeechSynthesisOptions {
  rate?: number; // Vitesse de lecture (0.1 à 10)
  pitch?: number; // Hauteur de voix (0 à 2)
  volume?: number; // Volume (0 à 1)
  voice?: SpeechSynthesisVoice | null; // Voix spécifique
  lang?: string; // Langue
}

interface UseSpeechSynthesisReturn {
  isSupported: boolean;
  isPlaying: boolean;
  isPaused: boolean;
  voices: SpeechSynthesisVoice[];
  currentVoice: SpeechSynthesisVoice | null;
  speak: (text: string, options?: SpeechSynthesisOptions) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setVoice: (voice: SpeechSynthesisVoice | null) => void;
  setRate: (rate: number) => void;
  setPitch: (pitch: number) => void;
  setVolume: (volume: number) => void;
}

// Mapping des codes de langue vers les codes de langue pour Speech Synthesis
const LANGUAGE_MAP: Record<string, string> = {
  fr: 'fr-FR',
  en: 'en-US',
  es: 'es-ES',
  zh: 'zh-CN',
};

export const useSpeechSynthesis = (
  currentLanguage?: string
): UseSpeechSynthesisReturn => {
  const [isSupported, setIsSupported] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [currentVoice, setCurrentVoice] = useState<SpeechSynthesisVoice | null>(
    null
  );
  const [currentUtterance, setCurrentUtterance] =
    useState<SpeechSynthesisUtterance | null>(null);

  // Fonction pour sélectionner la voix appropriée selon la langue
  const selectVoiceForLanguage = useCallback(
    (availableVoices: SpeechSynthesisVoice[], language?: string) => {
      if (!language) {
        // Par défaut, chercher une voix française
        return (
          availableVoices.find(
            (voice) =>
              voice.lang.startsWith('fr') || voice.name.includes('French')
          ) ||
          availableVoices[0] ||
          null
        );
      }

      const targetLang = LANGUAGE_MAP[language] || language;

      // Chercher une voix correspondant à la langue
      const matchingVoice = availableVoices.find(
        (voice) =>
          voice.lang.startsWith(targetLang.split('-')[0]) ||
          voice.lang === targetLang ||
          voice.name.toLowerCase().includes(language.toLowerCase())
      );

      return matchingVoice || availableVoices[0] || null;
    },
    []
  );

  // Vérifier le support de l'API
  useEffect(() => {
    const supported = 'speechSynthesis' in window;
    setIsSupported(supported);

    if (supported) {
      // Charger les voix disponibles
      const loadVoices = () => {
        const availableVoices = speechSynthesis.getVoices();
        setVoices(availableVoices);

        // Sélectionner la voix appropriée selon la langue actuelle
        const selectedVoice = selectVoiceForLanguage(
          availableVoices,
          currentLanguage
        );
        setCurrentVoice(selectedVoice);
      };

      // Les voix peuvent ne pas être immédiatement disponibles
      loadVoices();
      speechSynthesis.onvoiceschanged = loadVoices;

      return () => {
        speechSynthesis.onvoiceschanged = null;
      };
    }
  }, [currentLanguage, selectVoiceForLanguage]);

  const speak = useCallback(
    (text: string, options: SpeechSynthesisOptions = {}) => {
      if (!isSupported || !text.trim()) return;

      // Arrêter toute lecture en cours
      speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);

      // Déterminer la langue à utiliser
      const targetLang =
        options.lang ||
        (currentLanguage
          ? LANGUAGE_MAP[currentLanguage] || currentLanguage
          : null) ||
        currentVoice?.lang ||
        'fr-FR';

      // Appliquer les options
      utterance.rate = options.rate || 1;
      utterance.pitch = options.pitch || 1;
      utterance.volume = options.volume || 1;
      utterance.lang = targetLang;
      utterance.voice = options.voice || currentVoice;

      // Gérer les événements
      utterance.onstart = () => {
        setIsPlaying(true);
        setIsPaused(false);
      };

      utterance.onend = () => {
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentUtterance(null);
      };

      utterance.onerror = (event) => {
        console.error('Erreur de synthèse vocale:', event);
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentUtterance(null);
      };

      utterance.onpause = () => {
        setIsPaused(true);
      };

      utterance.onresume = () => {
        setIsPaused(false);
      };

      setCurrentUtterance(utterance);
      speechSynthesis.speak(utterance);
    },
    [isSupported, currentVoice, currentLanguage]
  );

  const pause = useCallback(() => {
    if (isSupported && speechSynthesis.speaking) {
      speechSynthesis.pause();
    }
  }, [isSupported]);

  const resume = useCallback(() => {
    if (isSupported && speechSynthesis.paused) {
      speechSynthesis.resume();
    }
  }, [isSupported]);

  const stop = useCallback(() => {
    if (isSupported) {
      speechSynthesis.cancel();
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentUtterance(null);
    }
  }, [isSupported]);

  const setVoice = useCallback((voice: SpeechSynthesisVoice | null) => {
    setCurrentVoice(voice);
  }, []);

  const setRate = useCallback(
    (rate: number) => {
      if (currentUtterance) {
        currentUtterance.rate = rate;
      }
    },
    [currentUtterance]
  );

  const setPitch = useCallback(
    (pitch: number) => {
      if (currentUtterance) {
        currentUtterance.pitch = pitch;
      }
    },
    [currentUtterance]
  );

  const setVolume = useCallback(
    (volume: number) => {
      if (currentUtterance) {
        currentUtterance.volume = volume;
      }
    },
    [currentUtterance]
  );

  return {
    isSupported,
    isPlaying,
    isPaused,
    voices,
    currentVoice,
    speak,
    pause,
    resume,
    stop,
    setVoice,
    setRate,
    setPitch,
    setVolume,
  };
};
